/**
 * Registro do service worker, com a atualização sob controle de quem atende.
 *
 * O modo é `prompt` e não `autoUpdate` de propósito: trocar a página embaixo de
 * um vendedor no meio de uma conversa descarta o que ele estava escrevendo. Aqui
 * o novo build apenas se anuncia; quem decide a hora é a pessoa.
 *
 * O estado vive num store para que a UI (`UpdatePrompt`) seja só apresentação —
 * o registro precisa acontecer no `main.tsx`, antes do React, senão o primeiro
 * `controllerchange` chega sem ninguém escutando.
 */
import { create } from "zustand";
import { registerSW } from "virtual:pwa-register";

/** De hora em hora, e também toda vez que o app volta ao primeiro plano.
 *  Um PWA instalado pode passar semanas sem um reload de verdade. */
const INTERVALO_DE_CHECAGEM = 60 * 60 * 1000;

interface PWAState {
  /** Existe build novo esperando a pessoa aceitar. */
  precisaAtualizar: boolean;
  /** O app terminou de ficar disponível offline (primeira instalação). */
  prontoOffline: boolean;
  /** Aplica a atualização e recarrega. Nulo antes do registro. */
  atualizar: (() => void) | null;
  dispensar: () => void;
}

export const usePWAStore = create<PWAState>((set) => ({
  precisaAtualizar: false,
  prontoOffline: false,
  atualizar: null,
  dispensar: () => set({ precisaAtualizar: false, prontoOffline: false }),
}));

export function registrarServiceWorker(): void {
  // Em dev o plugin não emite service worker (devOptions.enabled = false), e o
  // módulo virtual vira um no-op. A guarda evita ruído no console mesmo assim.
  if (!("serviceWorker" in navigator)) return;

  const aplicar = registerSW({
    onNeedRefresh() {
      usePWAStore.setState({ precisaAtualizar: true });
    },
    onOfflineReady() {
      usePWAStore.setState({ prontoOffline: true });
    },
    onRegisteredSW(_url, registro) {
      if (!registro) return;

      const checar = () => {
        // `update()` rejeita quando não há rede; sem o catch isso vira
        // unhandled rejection a cada volta do app em modo avião.
        void registro.update().catch(() => undefined);
      };

      setInterval(checar, INTERVALO_DE_CHECAGEM);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") checar();
      });
    },
  });

  usePWAStore.setState({
    atualizar: () => {
      usePWAStore.setState({ precisaAtualizar: false });
      void aplicar(true);
    },
  });
}
